
import { useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

export default function CartSidebar() {
  const { items, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, totalItems, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCartOpen]);

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout', {
      state: {
        cartItems: items,
        isCartCheckout: true,
      },
    });
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      class: 'Class',
      kit: 'Kit',
      package: 'Package',
      manual: 'Manual',
      bundle: 'Bundle',
    };
    return labels[type] || type;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isCartOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsCartOpen(false)}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 flex items-center justify-center">
              <i className="ri-shopping-bag-line text-stone-900 text-lg"></i>
            </div>
            <div>
              <h2 className="text-lg font-serif font-semibold text-stone-900">Your Cart</h2>
              <p className="text-xs text-stone-400">{totalItems} {totalItems === 1 ? 'item' : 'items'}</p>
            </div>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-stone-600 text-xl"></i>
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mb-4">
                <i className="ri-shopping-cart-line text-3xl text-stone-300"></i>
              </div>
              <p className="text-stone-500 mb-2">Your cart is empty</p>
              <p className="text-stone-400 text-sm">Add some amazing cocktail experiences!</p>
              <button
                onClick={() => setIsCartOpen(false)}
                className="mt-6 px-6 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-full hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-4 p-4 bg-stone-50 rounded-2xl group hover:bg-stone-100 transition-colors"
                >
                  {item.image && (
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-white">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">
                          {getTypeLabel(item.type)}
                        </span>
                        <h3 className="text-sm font-semibold text-stone-900 line-clamp-1">{item.name}</h3>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="w-6 h-6 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-rose-50"
                      >
                        <i className="ri-delete-bin-line text-rose-500 text-xs"></i>
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 bg-white rounded-full p-1">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line text-stone-600 text-xs"></i>
                        </button>
                        <span className="text-sm font-medium text-stone-900 w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line text-stone-600 text-xs"></i>
                        </button>
                      </div>
                      <p className="text-sm font-semibold text-stone-900">${item.price * item.quantity}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t border-stone-100">
            <div className="flex items-center justify-between mb-4">
              <span className="text-stone-500">Subtotal</span>
              <span className="text-2xl font-display text-stone-900">${totalPrice}</span>
            </div>
            <p className="text-xs text-stone-400 mb-4">Taxes calculated at checkout</p>
            <button
              onClick={handleCheckout}
              className="w-full py-4 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2"
            >
              <i className="ri-secure-payment-line"></i>
              Checkout — ${totalPrice}
            </button>
            <button
              onClick={clearCart}
              className="w-full mt-3 py-2.5 text-stone-400 text-sm hover:text-stone-600 transition-colors cursor-pointer"
            >
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
}
